import { PropsWithChildren, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Icon, Section, Text } from '@factorialco/deprecated-design-system'
import {
  BlockedPeriodPolicyFragment,
  UpdateBlockedPeriodPolicyDocument,
  UpdateBlockedPeriodPolicyMutation,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import type { Back } from 'modules/core/components/Card/types'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import { LeaveTypeSelectorInput } from 'modules/core/components/Inputs/LeaveTypeSelectorInput'
import TextInput from 'modules/core/components/Inputs/Text'
import NotFound from 'modules/core/components/NotFound'
import { Panes } from 'modules/core/components/Panes'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMutation } from 'modules/core/hooks/useMutation'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { usePrompt } from 'modules/core/lib/react-router'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import PolicyForm from 'modules/timeoff/lib/BlockedPeriods/PolicyUpdateForm'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

import Employees from './Employees'
import TimePeriods from './TimePeriods'

const Warning = observer(() => {
  return (
    <Box
      width="full"
      background="champagne400"
      padding="s8"
      flexDirection="row"
      justifyContent="center"
    >
      <Icon.Medium icon="info-circle-line" color="grey800" />
      <Text.Normal>{i18n.t('settings.timeoff_blocked_periods.detail.unsaved_changes')}</Text.Normal>
    </Box>
  )
})

interface Props {
  isNotFound: boolean
  loading: boolean
  policy: BlockedPeriodPolicyFragment | undefined
  back: Back
}

const BasicBlockedPeriodsPolicyInfo = ({
  isNotFound,
  loading,
  policy,
  children,
  back,
}: PropsWithChildren<Props>) => {
  const navigate = useNavigate()
  const [timePeriodsDirty, setTimePeriodsDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  const leaveTypeIds = policy?.leaveTypes?.map((leaveType) => leaveType.id) || []

  // eslint-disable-next-line unused-imports/no-unused-vars
  const timePeriods = (policy?.timePeriods || []).map(({ __typename, policyId, ...tp }) => ({
    ...tp,
    _destroy: false,
  }))

  const form: PolicyForm = useMemo(
    () =>
      new PolicyForm({
        leave_type_ids: leaveTypeIds,
        members: policy?.membersAsAccesses.map((member) => member.id) || [],
        name: policy?.name || '',
        strategy: policy?.strategy || 'all',
        time_periods: timePeriods,
        location_ids: policy?.locationIds || [],
        team_ids: policy?.teamIds || [],
        query: '',
        legal_entity_ids: policy?.legalEntityIds || [],
      }),
    [policy]
  )

  usePrompt(i18n.t('common.confirm_navigation'), () => form.isDirty && !form.saved)

  const { loading: fetchingData } = useAsyncData(() => Promise.all([leaveTypes.fetch()]), [])

  const trackEvent = () => {
    const eventProperties: TrackingProperties = {
      absencetype_count: form.get('leave_type_ids').value.length,
      employees: form.get('strategy').value,
    }

    if (!form.get('time_periods').value.length) {
      eventProperties.timeperiod_empty = true
    } else {
      if (form.hasTimeRangePeriods) eventProperties.timeperiod_range = true
      if (form.hasContractPeriods) eventProperties.timeperiod_contract = true
    }

    track(trackingEvents.timeoff.blockedPeriods.modifyBlockedPeriodSubmit, eventProperties)
  }

  const [updateBlockedPeriodPolicyMutation] = useMutation(UpdateBlockedPeriodPolicyDocument, {
    extractorFn: (mutationData: UpdateBlockedPeriodPolicyMutation) =>
      mutationData.timeoffMutations.updateTimeoffBlockedPeriod,
  })

  const onSubmit = async () => {
    setSaving(true)
    if (!form.isFqlMultiselect) {
      form.setValues({ team_ids: [], location_ids: [] })
    }

    if (!form.isMembers) {
      form.setValues({ members: [] })
    }

    if (!policy) {
      return
    }

    trackEvent()

    const result = await updateBlockedPeriodPolicyMutation({
      variables: {
        id: policy.id,
        leaveTypeIds: form.get('leave_type_ids').value,
        members: form.get('members').value,
        name: form.get('name').value,
        strategy: form.get('strategy').value,
        timePeriodsAttributes: form.get('time_periods').value,
        locationIds: form.get('location_ids').value,
        teamIds: form.get('team_ids').value,
        legalEntityIds: form.get('legal_entity_ids').value,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      setSaving(false)
      setTimePeriodsDirty(false)
    }

    return handled
  }

  const toBeSaved = () => {
    return (form.isDirty || form.get('time_periods').isDirty || timePeriodsDirty) && form.isValid
  }

  const onSelectLeaveTypes = (leaveTypeIds: string[]) => {
    form.setValues({
      leave_type_ids: leaveTypeIds,
    })
  }

  const visibleLeaveTypes = leaveTypes.active

  const backToPoliciesPage = () => {
    navigate(back.href)
  }

  return (
    <ResponsivePage.Body
      loading={loading || fetchingData || saving}
      error={false}
      showHeader={false}
      transactionalActions={{
        primary: {
          label: i18n.t('settings.timeoff_blocked_periods.detail.save_changes'),
          onClick: onSubmit,
          disabled: !toBeSaved(),
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: backToPoliciesPage,
        },
      }}
    >
      {toBeSaved() && <Warning />}
      {isNotFound ? (
        <NotFound />
      ) : (
        <Panes direction="vertical">
          <Section
            illustration="tracking-timer"
            title={i18n.t('settings.timeoff_blocked_periods.detail.title')}
            description={i18n.t('settings.timeoff_blocked_periods.detail.description')}
          >
            <Box padding="s24" borderRadius={{ all: 'abs008' }} background="grey200" gap="s24">
              <TextInput
                label={i18n.t('settings.timeoff_blocked_periods.fields.name')}
                field={form.get('name')}
              />
              <LeaveTypeSelectorInput
                label={i18n.t('time_off.allowances.page.labels.leave_types')}
                leaveTypes={visibleLeaveTypes}
                selectedIds={form.get('leave_type_ids').value}
                onSubmit={onSelectLeaveTypes}
                showCreateLeave={visibleLeaveTypes.length === 0}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'settings.timeoff_blocked_periods.detail.absence_types.tooltip'
                    )}
                    icon="question"
                  />
                }
              />
            </Box>
          </Section>
          <TimePeriods
            policyForm={form}
            onDelete={() => {
              setTimePeriodsDirty(true)
            }}
          />
          <Employees policyForm={form} policy={policy} loading={loading} />
          {children}
        </Panes>
      )}
    </ResponsivePage.Body>
  )
}

export default observer(BasicBlockedPeriodsPolicyInfo)
